// Downloadable resource library for the Resource Center.
// Every asset is generated client-side from these strings — no gated forms,
// no email capture, no backend. Keeps the page static and instantly cacheable.

export type ResourceFormat = "md" | "csv";

export type ResourceCategory =
  | "Technical SEO"
  | "Local SEO"
  | "AI Search"
  | "Content"
  | "Reporting";

export interface DownloadableResource {
  slug: string;
  title: string;
  summary: string;
  category: ResourceCategory;
  format: ResourceFormat;
  /** Approximate line/row count, shown as a weight signal on the card. */
  size: string;
  /** What an operator actually does with it. */
  useCases: string[];
  /** Raw file contents produced on download. */
  body: string;
}

const csv = (rows: string[][]) =>
  rows
    .map((r) => r.map((c) => (/[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c)).join(","))
    .join("\n");

export const RESOURCES: DownloadableResource[] = [
  {
    slug: "technical-seo-audit-checklist",
    title: "Technical SEO Audit Checklist",
    summary:
      "The 62-point checklist we run on every new engagement, ordered by the sequence we actually work in — crawl, index, render, speed, structure.",
    category: "Technical SEO",
    format: "csv",
    size: "62 rows",
    useCases: [
      "Scoping a technical audit before a rebuild",
      "Handing a prioritised defect list to a dev team",
      "Quarterly regression check on a live site",
    ],
    body: csv([
      ["Phase", "Check", "Why it matters", "Severity", "Owner"],
      ["Crawl", "robots.txt does not block CSS/JS", "Blocked assets break rendering and CWV measurement", "Critical", "Dev"],
      ["Crawl", "XML sitemap returns 200 and lists only indexable URLs", "Mixed sitemaps waste crawl budget", "High", "SEO"],
      ["Crawl", "No orphan pages in the sitemap", "Orphans rarely earn or pass authority", "Medium", "SEO"],
      ["Crawl", "Crawl depth of money pages <= 3 clicks", "Depth correlates with crawl frequency", "High", "SEO"],
      ["Crawl", "No infinite faceted-URL spaces", "Facets can consume the entire crawl budget", "Critical", "Dev"],
      ["Index", "Single canonical per URL, self-referencing by default", "Conflicting canonicals cause Google to pick its own", "Critical", "SEO"],
      ["Index", "Canonicals are absolute and point to the apex domain", "Protocol/host drift splits signals", "High", "Dev"],
      ["Index", "No noindex on pages present in the sitemap", "Contradictory signals suppress indexing", "Critical", "SEO"],
      ["Index", "Trailing-slash policy enforced with 301s", "Duplicate variants dilute link equity", "High", "Dev"],
      ["Index", "Pagination uses crawlable <a href> links", "JS-only pagination hides deep inventory", "High", "Dev"],
      ["Render", "Primary content present in server HTML", "Client-only content is discovered late or not at all", "Critical", "Dev"],
      ["Render", "Headings render without JavaScript", "Structure is a ranking and AEO signal", "High", "Dev"],
      ["Render", "Internal links are anchors, not click handlers", "Non-anchor navigation is not followed", "High", "Dev"],
      ["Render", "No hydration mismatch errors in console", "Mismatches can blank sections for crawlers", "Medium", "Dev"],
      ["Speed", "LCP element preloaded with fetchpriority=high", "Directly moves the LCP metric", "Critical", "Dev"],
      ["Speed", "Hero image served as AVIF/WebP with width+height", "Prevents layout shift and cuts bytes", "High", "Dev"],
      ["Speed", "Third-party tags deferred past first interaction", "Analytics/chat tags are the usual INP culprit", "High", "Dev"],
      ["Speed", "Fonts loaded non-blocking with display=swap", "Removes render-blocking round trips", "Medium", "Dev"],
      ["Speed", "Below-the-fold sections use content-visibility", "Skips layout/paint for offscreen work", "Medium", "Dev"],
      ["Speed", "CLS < 0.1 on mobile at 360px width", "Mobile CLS is where most sites fail", "High", "Dev"],
      ["Structure", "One H1 per page, matching search intent", "Clarifies the page's primary entity", "High", "Content"],
      ["Structure", "Heading levels never skip (H2 -> H4)", "Breaks outline parsing for assistive tech and LLMs", "Medium", "Content"],
      ["Structure", "Title <= 60 chars, description <= 155", "Prevents SERP truncation and CTR loss", "High", "SEO"],
      ["Structure", "BreadcrumbList schema on every deep page", "Improves SERP display and internal-context signals", "Medium", "SEO"],
      ["Structure", "Organization + LocalBusiness schema on the site", "Entity grounding for Knowledge Graph and AI answers", "High", "SEO"],
      ["Structure", "FAQPage schema only where FAQs are visible", "Invisible FAQ markup risks manual action", "High", "SEO"],
      ["Structure", "Article/BlogPosting schema with a real author entity", "E-E-A-T attribution across the publication", "Medium", "SEO"],
      ["Structure", "Image alt text describes content, not keywords", "Accessibility plus image-search coverage", "Medium", "Content"],
      ["Links", "No orphaned service or location pages", "Every commercial page needs contextual inbound links", "High", "SEO"],
      ["Links", "Internal anchors use descriptive text, not 'here'", "Anchor text is a primary relevance signal", "Medium", "Content"],
      ["Links", "No internal links to redirecting URLs", "Each hop wastes crawl and slows users", "Medium", "Dev"],
      ["Links", "404s from external backlinks are 301'd to the closest match", "Recovers otherwise lost authority", "High", "SEO"],
      ["Local", "NAP identical across site, GBP, and citations", "Inconsistency suppresses Map Pack placement", "Critical", "SEO"],
      ["Local", "Each location page has unique on-page content", "Templated city pages get filtered out", "Critical", "Content"],
      ["Local", "Embedded map is lazy-loaded", "Map iframes are a classic LCP/INP tax", "Medium", "Dev"],
      ["Local", "Service-area coverage is explicit in copy and schema", "Clarifies eligibility for local intent", "Medium", "SEO"],
      ["AI Search", "First 100 words answer the page's core question", "Answer-first blocks are what get extracted", "Critical", "Content"],
      ["AI Search", "Every claim has a source, date, or first-party datapoint", "Unsourced claims rarely get cited", "High", "Content"],
      ["AI Search", "Entities named explicitly, not by pronoun", "Reduces coreference ambiguity for LLMs", "Medium", "Content"],
      ["AI Search", "llms.txt published and linked", "Machine-readable map of the site's best sources", "Low", "Dev"],
      ["AI Search", "Author bio, credentials, and sameAs present", "Attribution strengthens citation likelihood", "Medium", "Content"],
      ["Analytics", "GA4 receives page_view on client-side route changes", "SPA route changes are silently lost otherwise", "High", "Dev"],
      ["Analytics", "Conversions defined as events, not destination URLs", "Destination goals miss modal and inline forms", "High", "Analytics"],
      ["Analytics", "Search Console verified for the exact canonical host", "Reports otherwise cover the wrong property", "Critical", "SEO"],
      ["Analytics", "Core Web Vitals monitored with field (RUM) data", "Lab scores hide real-device regressions", "High", "Dev"],
      ["Analytics", "Rankings tracked per market, not nationally averaged", "Local performance is invisible in national averages", "Medium", "SEO"],
      ["Security", "HTTPS enforced with HSTS", "Mixed content breaks trust and rendering", "Critical", "Dev"],
      ["Security", "www redirects to apex with a single 301", "Chained redirects lose speed and clarity", "High", "Dev"],
      ["Security", "Staging environments are noindex + auth-gated", "Staging duplicates outrank production more often than you'd think", "Critical", "Dev"],
      ["Security", "Admin routes are not linked or crawlable", "Reduces attack surface and index bloat", "High", "Dev"],
      ["Content", "No two pages target the same primary query", "Cannibalisation splits ranking signals", "Critical", "SEO"],
      ["Content", "Thin pages (<300 words) merged or removed", "Thin inventory drags site-level quality", "High", "Content"],
      ["Content", "Every pillar has >= 4 supporting cluster pages", "Topical coverage beats isolated long pages", "High", "Content"],
      ["Content", "Publication and updated dates visible and accurate", "Freshness affects both ranking and citation", "Medium", "Content"],
      ["Content", "Comparison and pricing questions answered on-page", "Bottom-funnel intent leaks to competitors otherwise", "High", "Content"],
      ["UX", "Primary CTA visible without scrolling on mobile", "Mobile CTR to contact drops sharply below the fold", "High", "Design"],
      ["UX", "Tap targets >= 44px with 8px spacing", "Mis-taps are a silent mobile conversion killer", "Medium", "Design"],
      ["UX", "Forms have <= 5 required fields", "Each extra field measurably reduces completion", "High", "Design"],
      ["UX", "Phone numbers are tel: links", "Mobile call intent should be one tap", "Medium", "Dev"],
      ["UX", "Contrast ratio >= 4.5:1 for body text", "Accessibility compliance and readability", "High", "Design"],
      ["QA", "Automated link audit runs in CI", "Catches breakage before users and crawlers do", "Medium", "Dev"],
      ["QA", "Schema validated on every deploy", "Malformed JSON-LD silently drops rich results", "High", "Dev"],
      ["QA", "Redirect map tested after every migration", "Migration losses are almost always redirect losses", "Critical", "SEO"],
    ]),
  },
  {
    slug: "local-seo-launch-plan",
    title: "Local SEO 90-Day Launch Plan",
    summary:
      "Week-by-week plan for a new location: profile build, citation core, review engine, on-page city pages, and the reporting cadence.",
    category: "Local SEO",
    format: "md",
    size: "13 weeks",
    useCases: [
      "Launching a new service area or second office",
      "Rebooting a neglected Google Business Profile",
      "Giving a client a credible first-quarter roadmap",
    ],
    body: `# Local SEO 90-Day Launch Plan
Optimize Plus — Austin, TX · https://optimizeplus.net/

## Weeks 1–2 · Foundation
- Claim or reclaim the Google Business Profile; verify by the fastest available method.
- Lock the canonical NAP string. Every future citation copies this exactly.
- Choose one primary category and no more than four secondary categories.
- Write the 750-character description around the primary service + city, not a slogan.
- Upload 20+ real photos: exterior, interior, team, work in progress, completed work.
- Set service areas explicitly; do not list every city in the state.

## Weeks 3–4 · On-page
- Build the city landing page: unique intro, local proof, neighbourhood coverage, FAQ.
- Add LocalBusiness schema with geo coordinates, hours, and sameAs profiles.
- Add BreadcrumbList schema and link the page from the locations hub and two services.
- Ensure the phone number is a tel: link and visible above the fold on mobile.

## Weeks 5–6 · Citation core
- Submit to the structural aggregators first, then the top 20 industry directories.
- Audit for duplicates and suppress rather than edit where the platform allows.
- Log every submission with URL, date, and the exact NAP used.

## Weeks 7–8 · Review engine
- Ship a one-tap review link into invoices, job-completion SMS, and email footers.
- Target a steady weekly rate. Bursts look manufactured and get filtered.
- Respond to every review inside 48 hours, naming the service and the city naturally.

## Weeks 9–10 · Proximity offset
- Publish neighbourhood pages for the two or three areas where you are outside the
  natural proximity radius. Each needs distinct landmarks, routes, and real jobs.
- Earn two or three genuinely local links: chamber, supplier, sponsorship, local press.

## Weeks 11–12 · Conversion
- Instrument call, form, and direction-request events in GA4.
- Add a mobile sticky call/quote bar to location pages.
- Cut form fields to the minimum that still qualifies a lead.

## Week 13 · Review and re-plan
- Compare Map Pack position by grid point, not a single average.
- Compare GBP calls, direction requests, and website clicks month over month.
- Rank the next 90 days by which grid points are closest to breaking into the top three.

## Reporting cadence
| Frequency | Report |
|---|---|
| Weekly | Grid rank snapshot, new reviews, new citations |
| Monthly | GBP insights, GSC local queries, conversion events |
| Quarterly | Competitive Map Pack analysis and plan reset |
`,
  },
  {
    slug: "ai-search-readiness-scorecard",
    title: "AI Search Readiness Scorecard",
    summary:
      "The four-axis scoring sheet behind the GEO Success Matrix™ — answer clarity, entity strength, topical placement, freshness — with scoring rules.",
    category: "AI Search",
    format: "csv",
    size: "24 rows",
    useCases: [
      "Pre-publish QA on pages that target AI Overviews",
      "Auditing an existing top-100 for citation readiness",
      "Building a rework backlog ranked by score gap",
    ],
    body: csv([
      ["Axis", "Signal", "Scoring rule (0-5)", "Weight"],
      ["Answer clarity", "First 100 words answer the query directly", "5 = complete answer, 0 = throat-clearing intro", "0.30"],
      ["Answer clarity", "Answer is self-contained without scrolling", "5 = standalone paragraph, 0 = requires whole page", "0.30"],
      ["Answer clarity", "Definition sentence uses subject-verb-object form", "5 = plain declarative, 0 = marketing phrasing", "0.30"],
      ["Answer clarity", "Lists and tables used for enumerable answers", "5 = structured, 0 = buried in prose", "0.30"],
      ["Answer clarity", "Reading grade between 8 and 12", "5 = in range, 0 = above 16 or below 6", "0.30"],
      ["Answer clarity", "Headings phrased as real user questions", "5 = all question headings, 0 = none", "0.30"],
      ["Entity strength", "Organization and author entities marked up", "5 = both with sameAs, 0 = neither", "0.25"],
      ["Entity strength", "Author has a profile page with credentials", "5 = full profile, 0 = byline only", "0.25"],
      ["Entity strength", "Brand mentioned consistently, never by pronoun alone", "5 = explicit throughout, 0 = ambiguous", "0.25"],
      ["Entity strength", "External corroboration exists (press, directories, profiles)", "5 = 5+ sources, 0 = none", "0.25"],
      ["Entity strength", "Location and service area are explicit", "5 = stated in copy and schema, 0 = implied", "0.25"],
      ["Entity strength", "Claims carry sources, dates, or first-party data", "5 = every claim, 0 = none", "0.25"],
      ["Topical placement", "Page sits inside a cluster of 4+ related pages", "5 = dense cluster, 0 = orphan", "0.25"],
      ["Topical placement", "Inbound internal links use descriptive anchors", "5 = 5+ descriptive links, 0 = none", "0.25"],
      ["Topical placement", "Pillar page links down and cluster links back up", "5 = bidirectional, 0 = one-way or none", "0.25"],
      ["Topical placement", "No competing page targets the same query", "5 = unique target, 0 = direct cannibalisation", "0.25"],
      ["Topical placement", "Adjacent subtopics covered somewhere on the site", "5 = full coverage, 0 = single page", "0.25"],
      ["Topical placement", "Breadcrumbs express the real hierarchy", "5 = accurate, 0 = missing", "0.25"],
      ["Freshness", "Content reviewed in the last 90 days", "5 = under 90 days, 0 = over 24 months", "0.20"],
      ["Freshness", "dateModified is accurate, not auto-bumped", "5 = reflects real edits, 0 = build-time stamp", "0.20"],
      ["Freshness", "Statistics carry the year they refer to", "5 = all dated, 0 = undated", "0.20"],
      ["Freshness", "Deprecated advice removed, not just appended", "5 = pruned, 0 = accumulating", "0.20"],
      ["Freshness", "Screenshots and examples are current", "5 = under 12 months, 0 = clearly dated UI", "0.20"],
      ["Freshness", "Change log or 'what changed' note present", "5 = maintained, 0 = absent", "0.20"],
    ]),
  },
  {
    slug: "content-brief-template",
    title: "Editorial Content Brief Template",
    summary:
      "The brief format we hand writers: intent, entity list, answer-first block, required sources, internal link targets, and the anti-slop rules.",
    category: "Content",
    format: "md",
    size: "1 page",
    useCases: [
      "Briefing freelance or in-house writers",
      "Standardising quality across a content team",
      "Keeping AI-assisted drafts out of generic territory",
    ],
    body: `# Content Brief — [Working title]

**Primary query:** 
**Search intent:** informational / commercial / transactional / navigational
**Target reader:** 
**Business outcome this page serves:** 
**Word count range:** 
**Author:** 
**Publish target:** 

## Answer-first block (first 100 words)
Write the complete answer to the primary query here, before anything else.
Plain declarative sentences. No preamble, no "in today's fast-paced world".

## Entities that must appear
- Brand:
- People:
- Places:
- Products/services:
- Competing or adjacent concepts:

## Required structure
| Section | Heading (phrase as a user question) | What it must contain |
|---|---|---|
| 1 | | |
| 2 | | |
| 3 | | |
| 4 | FAQ | 4–6 real questions from GSC, People Also Ask, or sales calls |

## Evidence requirements
- Every statistic needs a named source and a date.
- At least one first-party datapoint (client result, internal test, screenshot).
- No claim we could not defend on a sales call.

## Internal links (required)
| Anchor text | Destination | Why |
|---|---|---|
| | | |

## Schema to ship
- [ ] Article / BlogPosting with author entity
- [ ] BreadcrumbList
- [ ] FAQPage (only if the FAQ is visible on the page)

## Anti-slop rules
- No "in the ever-evolving landscape", "delve", "unlock", "game-changer".
- No sentence that would be true for any company in any industry.
- No section that exists only to hit a word count.
- Specific numbers, named tools, real timelines. If we can't be specific, cut it.

## Definition of done
- [ ] Answer-first block reads correctly on its own
- [ ] Reading grade 8–12
- [ ] Title <= 60 chars, description <= 155 chars
- [ ] All links resolve, no redirects
- [ ] Schema validates
`,
  },
  {
    slug: "monthly-seo-report-template",
    title: "Monthly SEO Report Template",
    summary:
      "The reporting skeleton we use with clients — outcomes first, then leading indicators, then the work log and next month's plan.",
    category: "Reporting",
    format: "csv",
    size: "28 rows",
    useCases: [
      "Replacing a vanity-metric dashboard with an outcome report",
      "Aligning an in-house team and leadership on one sheet",
      "Building an automated reporting template in Looker Studio",
    ],
    body: csv([
      ["Section", "Metric", "Source", "Cadence", "Why it's here"],
      ["Outcomes", "Qualified leads from organic", "CRM", "Monthly", "The only number leadership should judge the program on"],
      ["Outcomes", "Pipeline value from organic", "CRM", "Monthly", "Connects search to revenue, not sessions"],
      ["Outcomes", "Cost per qualified lead", "CRM + spend", "Monthly", "Comparable against paid channels"],
      ["Outcomes", "Phone calls from organic and GBP", "Call tracking", "Monthly", "Local intent converts by phone first"],
      ["Outcomes", "Form submissions by page", "GA4", "Monthly", "Shows which pages actually sell"],
      ["Leading", "Non-branded clicks", "GSC", "Monthly", "Branded clicks flatter every report"],
      ["Leading", "Non-branded impressions", "GSC", "Monthly", "Earliest visible signal of growth"],
      ["Leading", "Average position, top 20 commercial queries", "GSC", "Weekly", "Averaged site-wide position is meaningless"],
      ["Leading", "Queries in positions 4–15", "GSC", "Monthly", "The realistic short-term upside pool"],
      ["Leading", "Pages with at least one click", "GSC", "Monthly", "Measures how much of the site is actually working"],
      ["Leading", "New pages indexed", "GSC", "Monthly", "Publishing velocity that Google accepted"],
      ["Leading", "Map Pack grid coverage", "Local rank grid", "Monthly", "Local visibility is geographic, not a single number"],
      ["Leading", "AI Overview / assistant citations", "Manual + logs", "Monthly", "Emerging channel; track before it's material"],
      ["Leading", "Referring domains gained", "Ahrefs / Semrush", "Monthly", "Authority input, not an outcome"],
      ["Leading", "Reviews gained and average rating", "GBP", "Monthly", "Direct Map Pack and conversion input"],
      ["Health", "Core Web Vitals, mobile field data", "CrUX / RUM", "Monthly", "Field data, never lab-only"],
      ["Health", "Crawl errors and 5xx", "GSC", "Monthly", "Silent killers between releases"],
      ["Health", "Pages excluded by canonical choice", "GSC", "Monthly", "Early warning for duplication"],
      ["Health", "Broken internal links", "Crawl", "Monthly", "Cheap to fix, compounding cost if not"],
      ["Health", "Schema validation failures", "CI", "Per deploy", "Rich results disappear silently"],
      ["Work log", "Pages published", "Internal", "Monthly", "What was shipped, with links"],
      ["Work log", "Pages updated or pruned", "Internal", "Monthly", "Maintenance is half the program"],
      ["Work log", "Technical fixes deployed", "Internal", "Monthly", "Ties dev effort to search outcomes"],
      ["Work log", "Links and citations earned", "Internal", "Monthly", "With the actual URLs"],
      ["Next month", "Top three priorities", "Internal", "Monthly", "Forces a plan, not a retrospective"],
      ["Next month", "Blockers needing client action", "Internal", "Monthly", "Most delays are approval delays"],
      ["Next month", "Experiments running", "Internal", "Monthly", "Meta tests, layout tests, offer tests"],
      ["Next month", "Expected impact window", "Internal", "Monthly", "Sets honest expectations up front"],
    ]),
  },
  {
    slug: "site-migration-runbook",
    title: "Site Migration Runbook",
    summary:
      "Pre-launch, launch-day, and post-launch checklist for redesigns, replatforms, and domain moves — the phases where most traffic is lost.",
    category: "Technical SEO",
    format: "md",
    size: "3 phases",
    useCases: [
      "Redesign or replatform without losing rankings",
      "Domain or URL-structure change",
      "Consolidating multiple sites into one",
    ],
    body: `# Site Migration Runbook
Optimize Plus — Austin, TX · https://optimizeplus.net/

## Phase 1 · Before launch (T-30 to T-1)
- [ ] Full crawl of the live site; export every indexable URL.
- [ ] Export 16 months of GSC query and page data before it rolls off.
- [ ] Export top landing pages by conversions, not sessions.
- [ ] Export every backlinked URL, including 404s that still hold links.
- [ ] Build the redirect map: old URL -> new URL, one hop, no chains.
- [ ] Map every URL. "No equivalent" is a decision, not an omission.
- [ ] Preserve title, description, H1, and canonical for retained pages.
- [ ] Rebuild schema on the new templates and validate.
- [ ] Benchmark Core Web Vitals on staging vs production.
- [ ] Confirm staging is noindex AND auth-gated.
- [ ] Prepare the new XML sitemap; keep the old one ready to submit once.

## Phase 2 · Launch day
- [ ] Remove staging noindex and robots blocks. Verify with a live fetch.
- [ ] Spot-check 50 redirects across every URL pattern.
- [ ] Verify canonicals resolve to the apex host with the right slash policy.
- [ ] Submit the new sitemap in Search Console.
- [ ] Submit the old sitemap once so Google discovers the redirects fast.
- [ ] Verify analytics fires on the new templates, including route changes.
- [ ] Watch server logs for 404 and 5xx spikes for the first six hours.
- [ ] Request indexing for the top 10 commercial URLs.

## Phase 3 · After launch
**Days 1–7**
- [ ] Daily crawl-error review in GSC.
- [ ] Daily 404 log review; add redirects for anything with links or traffic.
- [ ] Confirm the index count trends toward the expected total.

**Weeks 2–4**
- [ ] Compare non-branded clicks against the pre-migration baseline.
- [ ] Fix redirect chains introduced by late content edits.
- [ ] Re-validate schema and CWV field data on real devices.

**Weeks 5–12**
- [ ] Expect partial volatility for 4–8 weeks; do not panic-revert.
- [ ] Rebuild internal links that were lost in template changes.
- [ ] Outreach on the highest-value backlinks now pointing at redirects.

## Rollback criteria
Revert only if non-branded clicks fall more than 35% for seven consecutive
days AND the cause is not an identified, fixable redirect or indexing defect.
`,
  },
];

export const RESOURCE_CATEGORIES: ResourceCategory[] = [
  "Technical SEO",
  "Local SEO",
  "AI Search",
  "Content",
  "Reporting",
];
